import type { Occupancy } from '@elara/domain/models/schemas/room-schemas';

import { buildPreviewMarkdownLink, isPreviewType } from '@elara/domain/schemas/preview-schemas';

import type { ToolBadgeSpec } from '../types.js';

import { formatOccupancy } from '#lib/occupancy-utils.js';

import { readArray, readField, readString } from '../extractors.js';

interface ItineraryItemToolOutput {
  /** Legacy: single item shape (pre-quantity) */
  item?: { title?: string | null; type?: string; referenceId?: string | null };
  /** New: array of created items (room multi-bookings produce N) */
  items?: Array<{ title?: string | null; type?: string; referenceId?: string | null }>;
}

/** Reads occupancy from the tool input's `metadata` field. */
function occupancyFromArgs(args: unknown): Occupancy | undefined {
  const meta = readField(args, 'metadata');
  const occ = readField(meta, 'occupancy');
  if (!occ || typeof occ !== 'object') return undefined;
  const adults = readField(occ, 'adults');
  if (typeof adults !== 'number') return undefined;
  const children = readField(occ, 'children');
  const childrenAges = readArray(readField(occ, 'childrenAges'));
  return {
    adults,
    ...(typeof children === 'number' ? { children } : {}),
    ...(childrenAges ? { childrenAges: childrenAges.filter((a) => typeof a === 'number') as number[] } : {}),
  };
}

function itineraryItemLabel(
  toolCall: { request?: { input?: unknown }; output?: unknown },
  isActive: boolean,
  presentTemplate: string,
  pastTemplate: string,
): string {
  const raw = toolCall.output as ItineraryItemToolOutput | undefined;
  // Prefer the new `items` array; fall back to the legacy single `item`.
  const itemsList = raw?.items ?? (raw?.item ? [raw.item] : []);
  const first = itemsList[0];
  const count = itemsList.length;
  const title = first?.title ?? (isActive ? 'something' : 'Something');
  const type = first?.type;
  const referenceId = first?.referenceId;
  const linkedTitle =
    referenceId != null && isPreviewType(type) ? buildPreviewMarkdownLink(title, type, referenceId) : title;

  // Suffix room items with the occupancy and (when > 1) the room count.
  let suffix = '';
  const input = toolCall.request?.input;
  if (type === 'room' || readString(readField(input, 'type')) === 'room') {
    const parts: string[] = [];
    if (count > 1) parts.push(`${count} rooms`);
    const occupancy = occupancyFromArgs(input);
    if (occupancy) parts.push(`for ${formatOccupancy(occupancy)}`);
    if (parts.length > 0) suffix = ` (${parts.join(', ')})`;
  }

  const renderedTitle = isActive ? title : linkedTitle;
  const template = isActive ? presentTemplate : pastTemplate;
  return `${template.replace('ITEM_NAME', renderedTitle)}${suffix}`;
}

export const addToItinerarySpec: ToolBadgeSpec = {
  toolName: 'addToItinerary',
  icon: 'event_upcoming',
  labelMarkdown: true,
  label: (tc, active) =>
    itineraryItemLabel(tc, active, 'Adding ITEM_NAME to itinerary', 'ITEM_NAME added to itinerary'),
  activitySummary: {
    label: (_, active) => (active ? 'Adding items to your itinerary' : 'Added items to your itinerary'),
  },
};

export const removeFromItinerarySpec: ToolBadgeSpec = {
  toolName: 'removeFromItinerary',
  icon: 'event_busy',
  labelMarkdown: true,
  label: (tc, active) =>
    itineraryItemLabel(tc, active, 'Removing ITEM_NAME from itinerary', 'ITEM_NAME removed from itinerary'),
  activitySummary: {
    label: (_, active) => (active ? 'Removing items from your itinerary' : 'Removed items from your itinerary'),
  },
};

export const updateItineraryItemSpec: ToolBadgeSpec = {
  toolName: 'updateItineraryItem',
  icon: 'event_available',
  labelMarkdown: true,
  label: (tc, active) => itineraryItemLabel(tc, active, 'Updating itinerary', 'ITEM_NAME updated in itinerary'),
  activitySummary: { label: (_, active) => (active ? 'Updating your itinerary' : 'Updated your itinerary') },
};

export const listItineraryItemsSpec: ToolBadgeSpec = {
  toolName: 'listItineraryItems',
  icon: 'event_upcoming',
  label: (_, active) => (active ? 'Listing itinerary' : 'Listed itinerary'),
};
